import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Swal from "sweetalert2";

function DistrictsTable(props) {
  const [rowData, setRowData] = useState([]);

  const fetchDistrictsData = useCallback(() => {
    axios
      .get("https://backend.form-seef.com/api/domiciles")
      .then((res) => {
        if (res.data && res.data.data) {
          const districts = res.data.data.map((item, index) => ({
            id: item.id,
            districtName: item.name,
          }));

          // Sort the districts alphabetically by district name
          districts.sort((a, b) => a.districtName.localeCompare(b.districtName));

          setRowData(districts);
          props.setFormDataLength(districts.length);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [props]);

  useEffect(() => {
    fetchDistrictsData();
  }, [fetchDistrictsData]);

  const handleAddDistrict = async () => {
    const { value: userInput } = await Swal.fire({
      title: "Add District",
      input: "text",
      inputPlaceholder: "District name",
      showCancelButton: true,
      confirmButtonText: "Add",
      inputValidator: (value) => {
        if (!value) {
          return "You need to enter a district name!";
        }
      },
    });

    if (userInput) {
      axios
        .post("https://backend.form-seef.com/api/domiciles", { name: userInput })
        .then(() => {
          Swal.fire("District added successfully");
          fetchDistrictsData();
        })
        .catch((error) => {
          console.log(error);
          Swal.fire("Error occurred while adding district");
        });
    }
  };

  const handleEditDistrict = (id) => {
    const existingDistrict = rowData.find((district) => district.id === id);
    if (existingDistrict) {
      Swal.fire({
        title: "Edit District",
        input: "text",
        inputValue: existingDistrict.districtName,
        inputPlaceholder: "Enter the district name",
        showCancelButton: true,
        inputValidator: (value) => {
          if (!value) {
            return "District name cannot be empty";
          }
        },
      }).then((result) => {
        if (result.isConfirmed) {
          const updatedDistrict = { id: existingDistrict.id, name: result.value };
          axios
            .put(`https://backend.form-seef.com/api/domiciles/${existingDistrict.id}`, updatedDistrict)
            .then(() => {
              Swal.fire("District updated successfully");
              fetchDistrictsData();
            })
            .catch((error) => {
              console.log(error);
              Swal.fire("Error occurred while updating district");
            });
        }
      });
    }
  };

  const handleDeleteDistrict = (id) => {
    const existingDistrict = rowData.find((district) => district.id === id);
    if (existingDistrict) {
      Swal.fire({
        title: "Delete District",
        text: `Are you sure you want to delete the district "${existingDistrict.districtName}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Delete",
      }).then((result) => {
        if (result.isConfirmed) {
          axios
            .delete(`https://backend.form-seef.com/api/domiciles/${existingDistrict.id}`)
            .then(() => {
              Swal.fire("District deleted successfully");
              fetchDistrictsData();
            })
            .catch((error) => {
              console.log(error);
              Swal.fire("Error occurred while deleting district");
            });
        }
      });
    }
  };

  const handleDeleteAllDistricts = () => {
    Swal.fire({
      title: "Delete All Districts",
      text: "Are you sure you want to delete all selected districts?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        const selectedDistricts = rowData.filter((district) => district.isSelected);
        const districtIds = selectedDistricts.map((district) => district.id);
        axios
          .delete("https://backend.form-seef.com/api/domiciles", { data: { ids: districtIds } })
          .then(() => {
            Swal.fire("Selected districts deleted successfully");
            fetchDistrictsData();
          })
          .catch((error) => {
            console.log(error);
            Swal.fire("Error occurred while deleting selected districts");
          });
      }
    });
  };

  const columns = [
    { field: "id", headerName: "Serial No", width: 70 },
    { field: "districtName", headerName: "Name of District", width: 550 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <div className="flex space-x-2">
          <button
            className="edit-button bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
            onClick={() => handleEditDistrict(params.row.id)}
          >
            Edit
          </button>
          <button
            className="delete-button bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
            onClick={() => handleDeleteDistrict(params.row.id)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const tableStyle = {
    height: 400,
    width: "100%",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: "4px",
  };

  const cellStyle = {
    borderBottom: "1px solid #ddd",
    borderRight: "1px solid #ddd",
  };

  return (
    <div>
      <div className="m-3">
        <div className="flex space-x-4 mb-2 mt-2">
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-semibold py-1 px-3 rounded"
            onClick={handleAddDistrict}
          >
            Add District
          </button>
          <button
            className="bg-red-500 hover:bg-red-700 text-white font-semibold py-1 px-3 rounded"
            onClick={handleDeleteAllDistricts}
          >
            Delete All Selected
          </button>
        </div>
      </div>
      <div style={tableStyle}>
        <DataGrid
          rows={rowData}
          columns={columns}
          pageSize={5}
          checkboxSelection
          components={{
            Toolbar: GridToolbar,
          }}
          rowStyle={cellStyle}
          headerClassName="table-header"
        />
      </div>
    </div>
  );
}

export default DistrictsTable;
